import { useRef, useCallback, useEffect } from "react";

interface UseRemoteAudioAnalysisProps {
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  isRecording: boolean;
  isConnected: boolean;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  startAudioRecordingRef: React.MutableRefObject<(() => Promise<void>) | null>;
  stopAudioRecordingRefForAutoMute: React.MutableRefObject<
    | ((
        shouldStopTracks?: boolean,
        options?: { finalizeTurn?: boolean; reason?: string }
      ) => Promise<void>)
    | null
  >;
  isPushToTalk?: boolean;
}

export const useRemoteAudioAnalysis = ({
  isMuted,
  setIsMuted,
  isRecording,
  isConnected,
  audioStreamRef,
  startAudioRecordingRef,
  stopAudioRecordingRefForAutoMute,
  isPushToTalk = false,
}: UseRemoteAudioAnalysisProps) => {
  const aiIsSpeakingRef = useRef(false);
  const userMutedManuallyRef = useRef(false);
  const aiSilenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const aiAudioAnalysisIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Function for automatically muting the microphone when AI is speaking
  const autoMuteWhenAISpeaking = useCallback(() => {
    if (isPushToTalk) return;

    if (!userMutedManuallyRef.current && !isMutedRef.current) {
      setIsMuted(true);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => {
          track.enabled = false;
        });
      }
      if (isRecording && stopAudioRecordingRefForAutoMute.current) {
        stopAudioRecordingRefForAutoMute.current(false, {
          finalizeTurn: true,
          reason: "ai_speaking",
        });
      }
    }
  }, [
    isPushToTalk,
    isRecording,
    setIsMuted,
    audioStreamRef,
    stopAudioRecordingRefForAutoMute,
  ]);

  // Function for automatically unmuting the microphone when AI stops speaking
  const autoUnmuteWhenAIStops = useCallback(() => {
    if (isPushToTalk) return;

    if (!userMutedManuallyRef.current) {
      const currentlyMuted = isMutedRef.current;
      if (currentlyMuted && !aiIsSpeakingRef.current) {
        setIsMuted(false);
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach((track) => {
            track.enabled = true;
          });
        }
        if (!isRecording && isConnected && startAudioRecordingRef.current) {
          startAudioRecordingRef.current();
        }
      }
    }
  }, [isPushToTalk, isRecording, isConnected, setIsMuted, audioStreamRef, startAudioRecordingRef]);

  const onAiSpeechDetectedRef = useRef<(() => void) | null>(null);
  const onAiSpeakingStateChangeRef = useRef<
    ((speaking: boolean) => void) | null
  >(null);

  // Function for analyzing the audio level of remote audio tracks
  const analyzeRemoteAudio = useCallback(() => {
    if (!analyserNodeRef.current) return;

    try {
      const bufferLength = analyserNodeRef.current.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      analyserNodeRef.current.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / bufferLength);
      const volume = rms * 100;

      const SPEAKING_THRESHOLD = 2;
      // Longer than a typical inter-sentence pause so karaoke is not cut short.
      const SILENCE_DURATION_MS = 1200;
      if (volume > SPEAKING_THRESHOLD) {
        if (!aiIsSpeakingRef.current) {
          aiIsSpeakingRef.current = true;
          onAiSpeechDetectedRef.current?.();
          onAiSpeakingStateChangeRef.current?.(true);
          if (aiSilenceTimeoutRef.current) {
            clearTimeout(aiSilenceTimeoutRef.current);
            aiSilenceTimeoutRef.current = null;
          }
          autoMuteWhenAISpeaking();
        } else if (aiSilenceTimeoutRef.current) {
          clearTimeout(aiSilenceTimeoutRef.current);
          aiSilenceTimeoutRef.current = null;
        }
      } else {
        if (aiIsSpeakingRef.current) {
          if (!aiSilenceTimeoutRef.current) {
            aiSilenceTimeoutRef.current = setTimeout(() => {
              if (aiIsSpeakingRef.current) {
                aiIsSpeakingRef.current = false;
                onAiSpeakingStateChangeRef.current?.(false);
                if (!userMutedManuallyRef.current) {
                  autoUnmuteWhenAIStops();
                }
              }
              aiSilenceTimeoutRef.current = null;
            }, SILENCE_DURATION_MS);
          }
        }
      }
    } catch (err) {
      console.warn("Error analyzing remote audio:", err);
    }
  }, [autoMuteWhenAISpeaking, autoUnmuteWhenAIStops]);

  const setupRemoteAudioAnalysis = useCallback(
    (
      audioTrack: any,
      options?: {
        onAiSpeechDetected?: () => void;
        onAiSpeakingStateChange?: (speaking: boolean) => void;
      },
    ) => {
      onAiSpeechDetectedRef.current = options?.onAiSpeechDetected ?? null;
      onAiSpeakingStateChangeRef.current =
        options?.onAiSpeakingStateChange ?? null;
      try {
        const mediaStreamTrack =
          audioTrack.mediaStreamTrack || audioTrack.track?.mediaStreamTrack;
        if (!mediaStreamTrack) {
          console.warn("No mediaStreamTrack found in audio track");
          return;
        }

        const stream = new MediaStream([mediaStreamTrack]);

        if (
          !audioContextRef.current ||
          audioContextRef.current.state === "closed"
        ) {
          audioContextRef.current = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
        }

        if (audioContextRef.current.state === "suspended") {
          audioContextRef.current.resume();
        }

        if (audioSourceRef.current) {
          audioSourceRef.current.disconnect();
          audioSourceRef.current = null;
        }

        audioSourceRef.current =
          audioContextRef.current.createMediaStreamSource(stream);

        if (!analyserNodeRef.current) {
          analyserNodeRef.current = audioContextRef.current.createAnalyser();
          analyserNodeRef.current.fftSize = 256;
          analyserNodeRef.current.smoothingTimeConstant = 0.8;
        }

        audioSourceRef.current.connect(analyserNodeRef.current);

        if (aiAudioAnalysisIntervalRef.current) {
          clearInterval(aiAudioAnalysisIntervalRef.current);
        }

        setTimeout(() => {
          aiAudioAnalysisIntervalRef.current = setInterval(() => {
            if (
              analyserNodeRef.current &&
              audioContextRef.current?.state === "running"
            ) {
              analyzeRemoteAudio();
            }
          }, 100);
        }, 200);
      } catch (err) {
        console.warn("Failed to setup remote audio analysis:", err);
      }
    },
    [analyzeRemoteAudio]
  );

  const cleanup = useCallback(() => {
    if (aiSilenceTimeoutRef.current) {
      clearTimeout(aiSilenceTimeoutRef.current);
      aiSilenceTimeoutRef.current = null;
    }
    if (aiAudioAnalysisIntervalRef.current) {
      clearInterval(aiAudioAnalysisIntervalRef.current);
      aiAudioAnalysisIntervalRef.current = null;
    }
    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }
    if (analyserNodeRef.current) {
      analyserNodeRef.current.disconnect();
      analyserNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (aiIsSpeakingRef.current) {
      aiIsSpeakingRef.current = false;
      onAiSpeakingStateChangeRef.current?.(false);
    }
    userMutedManuallyRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    setupRemoteAudioAnalysis,
    userMutedManuallyRef,
    cleanup,
  };
};
